import * as Sentry from '@sentry/react'
import { SENTRY_URL } from './api/endpoints.constants'

const SENSITIVE_FIELDS = new Set([
    'token',
    'refresh_token',
    'access_token',
    'id_token',
    'password',
    'authorization',
    'cookie',
    'set-cookie',
    'x-refresh-token',
])
const MAX_DEPTH = 8

const BEARER_RE = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g

const isPlainObject = (v: unknown): v is Record<string, unknown> => {
    if (v === null || typeof v !== 'object') return false
    if (Array.isArray(v)) return false
    if (typeof Blob !== 'undefined' && v instanceof Blob) return false
    if (typeof FormData !== 'undefined' && v instanceof FormData) return false
    if (typeof ArrayBuffer !== 'undefined' && v instanceof ArrayBuffer) return false
    return true
}

const maskTokensInString = (raw: string): string =>
    raw.replace(BEARER_RE, 'Bearer [Filtered]').replace(JWT_RE, '[Filtered]')

const scrubObject = (value: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): unknown => {
    if (typeof value === 'string') return maskTokensInString(value)
    if (value === null || typeof value !== 'object') return value
    if (depth >= MAX_DEPTH) return '[Truncated]'
    if (seen.has(value as object)) return '[Circular]'
    seen.add(value as object)
    if (Array.isArray(value)) return value.map((v) => scrubObject(v, seen, depth + 1))
    if (value instanceof Error) {
        // Clone (instead of mutating) so the caller's Error stays intact for
        // any downstream logging/retry. Preserve the prototype + own props so
        // subclasses like AxiosError don't lose response/config/code/cause —
        // Sentry's exception serializer relies on those for stack grouping
        // and request context.
        const cloned = Object.create(Object.getPrototypeOf(value)) as Error
        cloned.name = value.name
        cloned.message = maskTokensInString(value.message)
        if (value.stack) cloned.stack = maskTokensInString(value.stack)
        const clonedRecord = cloned as unknown as Record<string, unknown>
        for (const [k, v] of Object.entries(value)) {
            if (k === 'name' || k === 'message' || k === 'stack') continue
            clonedRecord[k] = SENSITIVE_FIELDS.has(k.toLowerCase())
                ? '[Filtered]'
                : scrubObject(v, seen, depth + 1)
        }
        const cause = (value as Error & { cause?: unknown }).cause
        if (cause !== undefined) {
            ;(cloned as Error & { cause?: unknown }).cause = scrubObject(cause, seen, depth + 1)
        }
        return cloned
    }
    if (!isPlainObject(value)) return value
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
        out[k] = SENSITIVE_FIELDS.has(k.toLowerCase())
            ? '[Filtered]'
            : scrubObject(v, seen, depth + 1)
    }
    return out
}

const scrubString = (raw: string): string => {
    try {
        return JSON.stringify(scrubObject(JSON.parse(raw)))
    } catch {
        // Not JSON — still mask token-shaped substrings (Bearer ..., JWTs).
        return maskTokensInString(raw)
    }
}

const scrubAny = (value: unknown): unknown =>
    typeof value === 'string' ? scrubString(value) : scrubObject(value)

Sentry.init({
    dsn: SENTRY_URL,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.data && typeof breadcrumb.data === 'object') {
            breadcrumb.data = scrubObject(breadcrumb.data) as Record<string, unknown>
        }
        if (typeof breadcrumb.message === 'string') {
            breadcrumb.message = maskTokensInString(breadcrumb.message)
        }
        return breadcrumb
    },
    beforeSend(event) {
        if (typeof event.message === 'string') {
            event.message = maskTokensInString(event.message)
        }
        if (event.request?.data !== undefined) {
            event.request.data = scrubAny(event.request.data)
        }
        if (event.breadcrumbs) {
            event.breadcrumbs = event.breadcrumbs.map((b) => {
                if (!b.data) return b
                return { ...b, data: scrubObject(b.data) as Record<string, unknown> }
            })
        }
        if (event.extra) {
            event.extra = scrubObject(event.extra) as Record<string, unknown>
        }
        if (event.contexts) {
            event.contexts = scrubObject(event.contexts) as typeof event.contexts
        }
        return event
    },
})
